/**
 * Pixel-art banknote icons, drawn once as crisp SVG data-URIs and shown via CSS classes (sized in
 * `em`, so they scale with the surrounding text). A GREEN paper bill = carried CASH; a CYAN
 * "digital" bill with a lit readout screen = BANK balance. Inject the style once, then drop
 * `cashIcon()` / `bankIcon()` inline anywhere a money amount is shown.
 */
const STYLE_ID = "ghostland-bill-style";

const W = 20, H = 12;
type Grid = (string | null)[][];

function blank(): Grid {
  return Array.from({ length: H }, () => Array<string | null>(W).fill(null));
}
function rect(g: Grid, x: number, y: number, w: number, h: number, c: string): void {
  for (let yy = y; yy < y + h; yy++) for (let xx = x; xx < x + w; xx++) {
    if (yy >= 0 && yy < H && xx >= 0 && xx < W) g[yy][xx] = c;
  }
}
function frame(g: Grid, x: number, y: number, w: number, h: number, c: string): void {
  rect(g, x, y, w, 1, c); rect(g, x, y + h - 1, w, 1, c);
  rect(g, x, y, 1, h, c); rect(g, x + w - 1, y, 1, h, c);
}

// A 5×7 "$" stamp (S-curve + centre stem), placed at (x0,y0) in colour `c`.
const DOLLAR: number[][] = [
  [1, 2, 3], [0, 2, 4], [0, 2], [1, 2, 3], [2, 4], [0, 2, 4], [1, 2, 3],
];
function dollar(g: Grid, x0: number, y0: number, c: string): void {
  DOLLAR.forEach((cols, row) => cols.forEach((col) => { if (g[y0 + row]) g[y0 + row][x0 + col] = c; }));
}

function svg(g: Grid): string {
  const rects: string[] = [];
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const c = g[y][x];
    if (c) rects.push(`<rect x='${x}' y='${y}' width='1' height='1' fill='${c}'/>`);
  }
  return `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='${W}' height='${H}' shape-rendering='crispEdges'>${rects.join("")}</svg>`,
  )}`;
}

// CASH — green banknote: dark edge, light inner frame, a lighter portrait medallion + white "$".
function cashGrid(): Grid {
  const g = blank();
  rect(g, 0, 0, W, H, "#2f8f49");          // paper
  frame(g, 0, 0, W, H, "#1b5e30");         // dark edge
  frame(g, 2, 2, W - 4, H - 4, "#63c47e"); // light inner frame
  rect(g, 6, 3, 8, 6, "#3aa45c");          // medallion plate
  frame(g, 6, 3, 8, 6, "#63c47e");
  dollar(g, 8, 2, "#eafff0");              // crisp white $
  return g;
}

// BANK — cyan "digital" banknote: same clean layout as the cash bill, but the medallion is a dark
// readout SCREEN with a glowing cyan "$" (reads as digital/stored money rather than paper cash).
function bankGrid(): Grid {
  const g = blank();
  rect(g, 0, 0, W, H, "#1f8fa6");          // paper
  frame(g, 0, 0, W, H, "#0e5566");         // dark edge
  frame(g, 2, 2, W - 4, H - 4, "#5fd6e8"); // light inner frame
  rect(g, 6, 3, 8, 6, "#06222a");          // dark screen plate
  frame(g, 6, 3, 8, 6, "#3aaecb");         // screen bezel
  dollar(g, 8, 2, "#7df0ff");              // glowing cyan $
  return g;
}

const CASH_URI = svg(cashGrid());
const BANK_URI = svg(bankGrid());

const CSS = `
.gl-bill-cash, .gl-bill-bank {
  display: inline-block; width: 1.5em; height: 0.9em; vertical-align: -0.08em;
  image-rendering: pixelated;
}
.gl-bill-cash { background: url("${CASH_URI}") center / contain no-repeat; }
.gl-bill-bank { background: url("${BANK_URI}") center / contain no-repeat; }
`;

export function injectBillStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

/** Inline HTML for the green cash bill (pair with `injectBillStyle()`). */
export function cashIcon(): string {
  return `<span class="gl-bill-cash"></span>`;
}

/** Inline HTML for the digital bank bill (pair with `injectBillStyle()`). */
export function bankIcon(): string {
  return `<span class="gl-bill-bank"></span>`;
}
